import { useTheme } from '@emotion/react'
import { RequiredAccess } from '@news-mono/web-common'
import { RequiredAccessLevel } from '@west-australian-newspapers/publication-types'
import React from 'react'
import { Theme } from '../../__styling'
import { BreakpointKeys } from '../../__styling/settings/breakpoints'
import { themedValue } from '../../__styling/themed-value'
import {
    StyledCommentsIcon,
    StyledKicker,
    StyledKickerText,
    StyledPremiumIcon,
} from '../../cards/Kicker/Kicker.styled'
import { IconCamera } from '../../icons/IconCamera/IconCamera'
import { IconVideo2 } from '../../icons/IconVideo2/IconVideo2'

export interface KickerModeOptions {
    kickerPosition?: KickerPosition
    size?: KickerSizeOptions
    kickerType?: KickerType
    kickerVisibility?: KickerVisibility
    icons?: Array<IconTypes> // included because of Profile Card - otherwise it's defined in CardHeadline
}

export type KickerMode = 'hidden' | KickerModeOptions

export type KickerType = 'block' | 'text'
export type IconTypes = 'premium' | 'video' | 'gallery' | 'comments'
export type KickerSizeOptions = 'default' | 'large'
export type KickerBreakpoints = 'initial' | 'xs' | 'sm' | 'md'
export type KickerVisibility =
    | 'hidden'
    | 'visible'
    | Partial<Record<'initial' | BreakpointKeys, 'hidden' | 'visible'>>

export type KickerStyleGuide = 'default' | 'olympics'

export type KickerPosition =
    | 'topleft'
    | 'topleftinset'
    | 'topright'
    | 'bottomleft'
    // Sevennews specific
    | 'bottomleftinset'
    | 'mobilebottomleft-insetdesktop'
    | 'bottomleftinset-hero'
    | 'bottomleftinset-herovideo'
    | undefined

export interface KickerProps {
    className?: string
    icons?: Array<IconTypes>
    kickerVisibility?: KickerVisibility
    size?: KickerSizeOptions
    kickerPosition?: KickerPosition
    kickerType?: KickerType
    requiredAccess?: RequiredAccess
    kickerStyleGuide?: KickerStyleGuide
}

const iconWidth = 14
const iconHeight = 14

export const Kicker: React.FC<KickerProps> = ({
    className,
    icons,
    children,
    requiredAccess,
    kickerPosition,
    kickerVisibility,
    kickerType,
    kickerStyleGuide = 'default',
    size = 'default',
}) => {
    const theme = useTheme()

    const themedKickerType = themedValue(theme, {
        thewest: kickerType || 'text',
        perthnow: 'block',
        sevennews: kickerType || 'text',
        fallback: undefined,
    }) as KickerType

    return (
        <StyledKicker
            className={className}
            size={size}
            kickerPosition={kickerPosition}
            kickerVisibility={kickerVisibility}
            kickerType={themedKickerType}
            kickerStyleGuide={kickerStyleGuide}
        >
            <StyledKickerText kickerStyleGuide={kickerStyleGuide}>
                {children}
            </StyledKickerText>
            {icons &&
                icons.length > 0 &&
                getPublicationIcon(
                    icons,
                    theme,
                    requiredAccess?.level ?? 'anonymous',
                )}
        </StyledKicker>
    )
}
Kicker.displayName = 'Kicker'

export const StyledIconCamera = 'StyledIconCamera'
export const StyledIconVideo2 = 'StyledIconVideo2'

const getPublicationIcon = (
    desiredIcons: Array<IconTypes>,
    theme: Theme,
    accessLevel: RequiredAccessLevel,
) => {
    const icons: JSX.Element[] = []

    desiredIcons.map((icon) => {
        switch (icon) {
            case 'comments': {
                if (theme.kind === 'thewest') {
                    icons.push(
                        <StyledCommentsIcon
                            fill={theme.colors.icons?.inverse}
                        />,
                    )
                }
                break
            }
            case 'gallery': {
                if (theme.kind === 'thewest') {
                    icons.push(
                        <IconCamera
                            width={iconWidth}
                            height={iconHeight}
                            className={StyledIconCamera}
                            fill={theme.colors.icons?.inverse}
                        />,
                    )
                }

                break
            }
            case 'video': {
                if (theme.kind === 'thewest') {
                    icons.push(
                        <IconVideo2
                            width={iconWidth}
                            height={iconHeight}
                            className={StyledIconVideo2}
                            fill={theme.colors.icons?.inverse}
                        />,
                    )
                }
                break
            }
            default:
                break
        }
    })

    // Premium icon is not gated to a masthead, but we only have premium on TW?
    // Premium icon always comes last
    if (desiredIcons.includes('premium') && accessLevel === 'subscriber') {
        icons.push(
            <StyledPremiumIcon
                width={iconWidth}
                height={iconHeight}
                fill={theme.colors.icons?.inverse}
            />,
        )
    }

    return icons
}
